/**
 * API client for the Order API
 */

import type { SessionResponse, ReturnResponse, CartItem, SubscriptionsResponse, ReorderParams, QuoteValidationResult } from "./types";

const API_BASE = "";

/**
 * API Error class for structured error handling
 */

class ApiError extends Error {
  constructor(
    public status: number,
    public code: string,
    message: string
  ) {
    super(message);
    this.name = "ApiError";
  }
}

/**
 * Fetch the current PunchOut session
 */
export async function getSession(): Promise<SessionResponse> {
  const res = await fetch(`${API_BASE}/api/punchout/session`, {
    credentials: "include",
  });

  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new ApiError(res.status, body.error || "unknown", body.message || "Failed to load session");
  }

  return res.json();
}

/**
 * Submit cart and get cXML for browser form post
 */
export async function submitCart(items: CartItem[], admin?: {
  adminFirstName: string;
  adminLastName: string;
  adminEmail: string;
}): Promise<ReturnResponse> {
  const res = await fetch(`${API_BASE}/api/punchout/return`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify({ items, ...admin }),
  });

  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new ApiError(res.status, body.error || "unknown", body.message || "Failed to submit cart");
  }

  return res.json();
}

/**
 * Cancel the session and get cXML for browser form post
 */
export async function cancelSession(): Promise<ReturnResponse> {
  const res = await fetch(`${API_BASE}/api/punchout/cancel`, {
    method: "POST",
    credentials: "include",
  });

  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new ApiError(res.status, body.error || "unknown", body.message || "Failed to cancel session");
  }

  return res.json();
}

/**
 * Submit form to UniMarket via browser POST
 */
export function postToUniMarket(targetUrl: string, cxmlPayload: string): void {
  const form = document.createElement("form");
  form.method = "POST";
  form.action = targetUrl;

  const input = document.createElement("input");
  input.type = "hidden";
  input.name = "cxml-urlencoded";
  input.value = cxmlPayload;

  form.appendChild(input);
  document.body.appendChild(form);
  form.submit();
}

/**
 * Validate reorder link parameters (HMAC signature and expiration)
 * Returns school subscriptions if valid
 */
export async function validateReorderLink(params: ReorderParams): Promise<{
  valid: boolean;
  reason?: string;
  subscriptions?: SubscriptionsResponse;
}> {
  // Check expiration client-side first
  const expTimestamp = parseInt(params.exp, 10);
  if (isNaN(expTimestamp)) {
    return { valid: false, reason: "Invalid expiration format" };
  }

  if (Date.now() / 1000 > expTimestamp) {
    return { valid: false, reason: "This link has expired" };
  }

  // Validate signature and get subscriptions via API
  const queryParams = new URLSearchParams({
    school: params.school,
    exp: params.exp,
    sig: params.sig,
  });

  const res = await fetch(`${API_BASE}/api/subscriptions/validate-reorder?${queryParams}`, {
    method: "GET",
  });

  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    if (res.status === 401 || res.status === 403) {
      return { valid: false, reason: body.message || "Invalid or expired link" };
    }
    throw new ApiError(res.status, body.error || "unknown", body.message || "Failed to validate link");
  }

  const subscriptions: SubscriptionsResponse = await res.json();
  return { valid: true, subscriptions };
}

/**
 * Get subscriptions for a school using JWT auth
 * Used when customer is already authenticated
 */
export async function getSubscriptions(jwtToken: string): Promise<SubscriptionsResponse> {
  const res = await fetch(`${API_BASE}/api/subscriptions`, {
    headers: {
      Authorization: `Bearer ${jwtToken}`,
    },
  });

  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new ApiError(res.status, body.error || "unknown", body.message || "Failed to load subscriptions");
  }

  return res.json();
}

/**
 * Validate quote link parameters (HMAC signature and data)
 * Returns pre-populated cart data if valid
 */
export async function validateQuoteLink(data: string, sig: string): Promise<QuoteValidationResult> {
  const queryParams = new URLSearchParams({
    d: data,
    s: sig,
  });

  const res = await fetch(`${API_BASE}/api/orders/validate-quote?${queryParams}`, {
    method: "GET",
  });

  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new ApiError(res.status, body.error || "unknown", body.message || "Failed to validate quote link");
  }

  return res.json();
}

export { ApiError };
